import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import DynamicCrudPage from '../../components/DynamicCrudPage';
import {
  useFirms,
  useCompanies,
  useFirmManagementPermissions,
  useAuth,
  SCREENS,
} from '../../hooks';
import AddIcon from '@mui/icons-material/Add';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import { TURKISH_CITIES } from '../../constants/turkishCities';
import { ROW_ACTIONS } from '../../constants/tableConfigurations';
import FirmCommunicationReportModal from './FirmCommunicationReportModal';

const FirmManagementPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const firmManagementPermissions = useFirmManagementPermissions();
  const { user } = useAuth();
  const [communicationReportOpen, setCommunicationReportOpen] = useState(false);

  const canViewCommunicationReport =
    user?.role === 'ROOT' || user?.canViewCommunicationReport === true;

  const customFormFields = useCallback((fields, additionalData, isRootUser) => {
    const { firms, companies } = additionalData;
    const companiesList = companies?.companies || firms?.companies || [];
    const partners = firms?.partners || [];
    const sectors = firms?.sectors || [];
    const referenceFirms = firms?.referenceFirms || [];

    return fields
      .map(field => {
        if (field.name === 'companyId') {
          return {
            ...field,
            options: companiesList.map(c => ({ value: c.id, label: c.name })),
            showIf: () => isRootUser,
          };
        }
        if (field.name === 'sector_ids') {
          return {
            ...field,
            options: sectors.map(s => ({ value: s.id, label: s.sector_name })),
          };
        }
        if (field.name === 'firm_city') {
          return {
            ...field,
            options: TURKISH_CITIES,
          };
        }
        if (field.name === 'partner_ids') {
          return {
            ...field,
            options: partners.map(p => ({
              value: p.id,
              label: p.partner_name,
            })),
          };
        }
        if (field.name === 'reference_firm_id') {
          return {
            ...field,
            options: referenceFirms.map(f => ({
              value: f.id,
              label: `${f.firm_name} (${f.firm_short_name})`,
            })),
          };
        }
        return field;
      })
      .filter(field => (field.name === 'companyId' ? isRootUser : true));
  }, []);

  const customFilterConfigs = useCallback((filters, data, isRootUser) => {
    return filters.filter(f => (f.showIfRoot ? isRootUser : true));
  }, []);

  const transformEditData = useCallback(firmData => {
    if (!firmData) return {};

    const isEstablished =
      firmData.is_established === 'yes' || firmData.is_established === true;
    const isPartnerReferral =
      firmData.partner_referral === 'yes' || firmData.partner_referral === true;

    return {
      ...firmData,
      companyId: firmData.company_id,
      is_established: isEstablished ? 'yes' : 'no',
      partner_referral: isPartnerReferral ? 'yes' : 'no',
      sector_ids: Array.isArray(firmData.sector_ids) ? firmData.sector_ids : [],
      partner_ids: Array.isArray(firmData.partner_ids)
        ? firmData.partner_ids
        : [],
      reference_firm_id: firmData.reference_firm_id,
      reference_text: firmData.reference_text,
      reference_type: firmData.reference_firm_id
        ? 'existing'
        : firmData.reference_text
          ? 'new'
          : 'none',
    };
  }, []);

  const customRowActions = useCallback(
    row => {
      const baseActions = ROW_ACTIONS.firms(row, firmManagementPermissions);
      return [
        {
          type: 'firm-detail',
          label: 'firms.actions.detail',
          icon: 'visibility',
          color: 'primary',
        },
        ...baseActions,
      ];
    },
    [firmManagementPermissions]
  );

  const handleRowAction = useCallback(
    (actionType, row) => {
      if (actionType === 'firm-detail') {
        navigate(`/firm-management/${row.id}`);
        return true;
      }
      if (actionType === 'firm-cards') {
        navigate(`/firm-management/${row.id}/cards`);
        return true;
      }
      return false;
    },
    [navigate]
  );

  const headerCommunicationReportButton = canViewCommunicationReport ? (
    <Button
      variant="contained"
      size="small"
      startIcon={<ContactPageIcon />}
      onClick={() => setCommunicationReportOpen(true)}
      sx={{ mr: 1, textTransform: 'none', boxShadow: 'none' }}
    >
      {t('firms.communicationReport.button')}
    </Button>
  ) : null;

  return (
    <>
      <DynamicCrudPage
        entityType="firms"
        screen={SCREENS.FIRM_MANAGEMENTS}
        commentEntityType="firm"
        hook={useFirms}
        additionalHooks={{
          companies: useCompanies,
          firms: useFirms,
          firmManagementPermissions: useFirmManagementPermissions,
        }}
        addButtonTextKey="table.actions.addFirm"
        addButtonIcon={<AddIcon />}
        entityTypeForDataTable="firm"
        customFormFields={customFormFields}
        customFilterConfigs={customFilterConfigs}
        customTransformEditData={transformEditData}
        customRowActions={customRowActions}
        onRowAction={handleRowAction}
        showAddButton={firmManagementPermissions.canCreate}
        headerExtraContent={headerCommunicationReportButton}
      />
      {canViewCommunicationReport && (
        <FirmCommunicationReportModal
          open={communicationReportOpen}
          onClose={() => setCommunicationReportOpen(false)}
        />
      )}
    </>
  );
};

export default FirmManagementPage;
